/* eslint-disable @typescript-eslint/no-shadow */
/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import LanguageIcon from '@material-ui/icons/Language';
import React, { useMemo } from 'react';
import { EntityLinksEmptyState } from './EntityLinksEmptyState';
import { LinksGridList } from './LinksGridList';
import { ColumnBreakpoints } from './types';
import { IconComponent, useApp, useRouteRef } from '@backstage/core-plugin-api';
import { InfoCard, InfoCardVariants } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogTranslationRef } from '../translation';
import { viewTechDocRouteRef } from '../../routes';
import { DEFAULT_NAMESPACE, RELATION_HAS_PART } from '@backstage/catalog-model';
import { Box, makeStyles, Typography } from '@material-ui/core';

/** @public */
export interface EntityLinksCardProps {
  cols?: ColumnBreakpoints | number;
  variant?: InfoCardVariants;
}

const useStyles = makeStyles(theme => ({
  groupedListBox: {
    '&:not(:first-child) .MuiTypography-root': {
      marginTop: theme.spacing(1),
    },
  },
}));

const TECHDOCS_ANNOTATION = 'backstage.io/techdocs-ref';
const TECHDOCS_EXTERNAL_ANNOTATION = 'backstage.io/techdocs-entity';
const OTHERS = 'Others';

const groupBy = <T, K>(array: T[], keyFn: (item: T) => K): Map<K, T[]> => {
  return array.reduce((map, item) => {
    const key = keyFn(item);
    return map.set(key, [...(map.get(key) || []), item]);
  }, new Map<K, T[]>());
};

export const EntityLinksCard = (props: EntityLinksCardProps) => {
  const { cols = undefined, variant } = props;
  const classes = useStyles();
  const { entity } = useEntity();
  const { entities = [] } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'component',
  });
  const app = useApp();
  const viewTechDocLink = useRouteRef(viewTechDocRouteRef);
  const { t } = useTranslationRef(catalogTranslationRef);
  const techDocsRef = entities.filter(
    ({ metadata: { annotations } }) =>
      annotations?.[TECHDOCS_ANNOTATION] ||
      annotations?.[TECHDOCS_EXTERNAL_ANNOTATION],
  );

  const { links, groupedLinks, hasOnlyOthers, iconResolver } = useMemo(() => {
    const techDocsLinks = techDocsRef.map(ref => ({
      url: viewTechDocLink({
        namespace: ref.metadata.namespace || DEFAULT_NAMESPACE,
        kind: ref.kind,
        name: ref.metadata.name,
      }),
      title: ref.metadata.name,
      icon: 'docs',
      type: 'TechDocs',
    }));

    const entityLinks = entity?.metadata?.links ?? [];

    const iconResolver = (key?: string): IconComponent =>
      key ? app.getSystemIcon(key) ?? LanguageIcon : LanguageIcon;

    const links = [...techDocsLinks, ...entityLinks];
    const grouped = groupBy(links, item => item?.type ?? OTHERS);
    const groupedLinks = Array.from(grouped.entries()).sort(
      ([typeA], [typeB]) => {
        // Move Others to end of the list
        if (typeA === OTHERS) return 1;
        if (typeB === OTHERS) return -1;
        return 0;
      },
    );
    const hasOnlyOthers = groupedLinks.every(([type]) => type === OTHERS);

    return {
      links,
      groupedLinks,
      hasOnlyOthers,
      iconResolver,
    };
  }, [app, entity?.metadata?.links, techDocsRef, viewTechDocLink]);

  return (
    <InfoCard title={t('entityLinksCard.title')} variant={variant}>
      {!links || links.length === 0 ? (
        <EntityLinksEmptyState />
      ) : (
        <Box>
          {groupedLinks.map(([type, groupItems]) => (
            <Box className={classes.groupedListBox} key={type}>
              <Typography
                variant="h6"
                component="h3"
                gutterBottom
                hidden={hasOnlyOthers}
              >
                {type}
              </Typography>
              <LinksGridList
                cols={cols}
                items={groupItems.map(({ url, title, icon }) => ({
                  text: title ?? url,
                  href: url,
                  Icon: iconResolver(icon),
                }))}
              />
            </Box>
          ))}
        </Box>
      )}
    </InfoCard>
  );
};
